/**
 * Operating-system / app-surface detection for analytics.
 *
 * This is the *device* platform (iOS / Android / Web) — distinct from the
 * multi-tenant "platform" (a BigNerve tenant identified by a slug). Used to
 * tag analytics events so journeys can be segmented by platform.
 *
 * All functions are SSR-safe and no-op to sensible defaults when `window` /
 * `navigator` are unavailable.
 */

/**
 * Detect the operating system the app is running on.
 *
 * @returns {'ios' | 'android' | 'web'}
 */
export function detectOS() {
    if (typeof navigator === 'undefined') {
        return 'web';
    }

    const ua = navigator.userAgent || '';

    // iPadOS 13+ presents a desktop Safari user-agent ("Macintosh"); fall back
    // to touch detection to classify it as iOS rather than web.
    const isIPadOS = /Macintosh/i.test(ua)
        && typeof document !== 'undefined'
        && 'ontouchend' in document;

    if (/iPhone|iPad|iPod/i.test(ua) || isIPadOS) {
        return 'ios';
    }

    if (/Android/i.test(ua)) {
        return 'android';
    }

    return 'web';
}

/**
 * Whether the app is running as an installed PWA (standalone display mode).
 *
 * @returns {boolean}
 */
export function isStandalonePWA() {
    if (typeof window === 'undefined') {
        return false;
    }

    const standaloneDisplay = typeof window.matchMedia === 'function'
        && window.matchMedia('(display-mode: standalone)').matches;

    // iOS Safari exposes a non-standard `navigator.standalone` flag.
    const iosStandalone = typeof navigator !== 'undefined' && navigator.standalone === true;

    return Boolean(standaloneDisplay || iosStandalone);
}
